import React from 'react'
import { capitalize, get } from 'lodash'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CompanyConfigProperties from 'src/companies/configurations/properties'
import CompanyConfiguration from 'src/companies/configurations/company-configuration'
import CompanyConfigAdminUsers from 'src/companies/configurations/admin-users'
import CompanyConfigMetadata from 'src/companies/configurations/metadata'
import CompanyConfigAgtraxApi from 'src/companies/configurations/agtrax-api'
import { centreAPIs } from 'src/utils/api'
import * as queries from 'src/utils/queries'
import { Box, Placeholders } from 'src/ui'
import { useToast } from 'src/utils/toast'
import AgtraxProcessorPanel from './agtrax-processors'
import { saveCompanyArgs } from 'src/companies/configurations.d'
import { Alert, AlertIcon } from '@chakra-ui/react'
import TenantConfigPanel from './tenant'

export const useCompanyConfig = ({ companySlug }) => {
  return useQuery<CompanyConfig, [string, { companySlug: string }]>({
    queryKey: ['company-config', { companySlug }],
    queryFn: async () => {
      let [err, response] = await centreAPIs.getCentreCompanyConfigBySlug({ slug: companySlug })

      if (err) throw err
      return response.data.config
    },
    gcTime: 0,
  })
}

export const useConfigConfigurables = () => {
  return useQuery<CompanyConfigurables, [string]>({
    queryKey: ['config-configurables'],
    queryFn: async () => {
      let [err, response] = await centreAPIs.getCentreConfigurables()

      if (err) throw err
      return response.data.configurables
    },

    gcTime: 0,
  })
}

const fieldGroupToArray = (fields: ConfigurablesGroup) =>
  Object.keys(fields).map((key) => ({ key, ...fields[key] }))

interface CompanyConfigProps {
  company: Company
  // route props
  env: string
  slug: string
}

const CompanyConfig: React.FC<CompanyConfigProps> = ({ company }) => {
  let toast = useToast()
  const queryClient = useQueryClient()

  const saveCompanyConfig = async ({ companyConfig }: saveCompanyArgs) => {
    let [err, response] = await centreAPIs.updateCentreCompanyConfig({
      slug: company.slug,
      companyConfig,
    })

    if (err) throw err
    return [err, response]
  }

  let companyConfigQuery = useCompanyConfig({ companySlug: company.slug })
  let configConfigurablesQuery = useConfigConfigurables()
  let { mutateAsync: saveCompanyConfigMutation } = useMutation({
    mutationFn: saveCompanyConfig,
    onSuccess: async (_res, { onSuccess }) => {
      await queryClient.invalidateQueries({ queryKey: ['company-config'] })
      toast({ title: 'Success', description: 'Updated company config' })
      if (onSuccess) onSuccess()
    },
    onError: () => {
      toast({
        status: 'error',
        description: 'Failed to update company config',
      })
    },
  })

  return (
    <>
      {company.is_child ? (
        <Alert status="info" my={8}>
          <AlertIcon />
          You are viewing a partner company. Some configuration options are not available as they
          only apply to the parent company.
        </Alert>
      ) : (
        <Box mt={8} />
      )}

      <CompanyConfigProperties company={company} />
      {queries.areAnyLoading(companyConfigQuery, configConfigurablesQuery) && (
        <Placeholders.LoadingState mt={8} />
      )}
      {queries.areAnyFailed(companyConfigQuery, configConfigurablesQuery) && (
        <Placeholders.FailedState mt={8} />
      )}
      {queries.areAllLoaded(companyConfigQuery, configConfigurablesQuery) && (
        <>
          {[{ path: 'features' }, { path: 'custom' }, { path: 'advanced', numColumns: 2 }].map(
            ({ path, ...props }) => (
              <CompanyConfiguration
                key={path}
                label={capitalize(path)}
                company={company}
                companyConfig={companyConfigQuery.data || {}}
                formFields={fieldGroupToArray(
                  get(configConfigurablesQuery, `data.${path}`, [] as any)
                )}
                saveCompanyConfig={saveCompanyConfigMutation}
                numColumns={props.numColumns}
              />
            )
          )}

          {!company.is_child && (
            <>
              <TenantConfigPanel
                baseUri="/api/aggregator/config/v2"
                title="Web Portal/Aggregator"
              />
              <TenantConfigPanel baseUri="/api/agretail/config/v1" title="Ag Retail" />
              <TenantConfigPanel baseUri="/api/grain/config/v1" title="Grain" />
              <TenantConfigPanel baseUri="/api/payments/v1" title="Payments" />
              <TenantConfigPanel baseUri="/api/platform/message/config/v1" title="Notify" />
              <TenantConfigPanel baseUri="/api/trade/esign/v1" title="Esign" />
              <TenantConfigPanel baseUri="/api/platform/tenant/config/v1" title="Tenant" />
              <TenantConfigPanel baseUri="/api/crm/config/v1" title="CRM" />
            </>
          )}

          <AgtraxProcessorPanel company={company} />

          <CompanyConfigAgtraxApi company={company} />

          <CompanyConfigMetadata company={company} />

          <CompanyConfigAdminUsers company={company} />
        </>
      )}
    </>
  )
}

export default CompanyConfig
